import React, { useRef, useState } from 'react';
import { Download, Upload, FileSpreadsheet, X } from 'lucide-react';
import Button from './Button.js';
import Alert from './Alert.js';

const BulkExcelModal = ({
  open,
  onClose,
  title,
  description,
  onDownloadTemplate,
  downloadLabel = 'Download empty Excel',
  onDownloadCurrent,
  currentLabel = 'Download current Excel',
  onUploadFile,
  onImported,
}) => {
  const inputRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState(null);

  if (!open) return null;

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await onDownloadTemplate();
      Alert.success('Empty Excel template downloaded');
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!String(file.name).toLowerCase().endsWith('.xlsx')) {
      Alert.error('Upload a .xlsx file from the template');
      return;
    }
    try {
      setUploading(true);
      setFileName(file.name);
      const res = await onUploadFile(file);
      const data = res?.data || {};
      setResult(data);
      Alert.success(data.message || 'Import finished');
      if (onImported) onImported(data);
    } catch (err) {
      Alert.error(err.response?.data?.error || err.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const skipped = result?.skipped || [];
  const failed = result?.failed || [];

  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-app-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-blue-600" />
              {title}
            </h2>
            <p className="text-sm text-gray-500 mt-1">{description}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          {onDownloadCurrent ? (
            <Button
              type="button"
              variant="secondary"
              icon={Download}
              fullWidth
              loading={downloading}
              onClick={async () => {
                try {
                  setDownloading(true);
                  await onDownloadCurrent();
                  Alert.success('Excel downloaded');
                } catch (err) {
                  Alert.error(err.response?.data?.error || err.message || 'Failed to download Excel');
                } finally {
                  setDownloading(false);
                }
              }}
            >
              {currentLabel}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="secondary"
            icon={Download}
            fullWidth
            loading={downloading}
            onClick={handleDownload}
          >
            {downloadLabel}
          </Button>
          <div className="relative">
            <Button
              type="button"
              variant="primary"
              icon={Upload}
              fullWidth
              loading={uploading}
              className="pointer-events-none"
            >
              Upload filled Excel
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream"
              disabled={uploading}
              className="absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0"
              onChange={(e) => handleUpload(e.target.files?.[0])}
            />
          </div>
          {fileName ? <p className="text-xs text-gray-500 truncate">Last file: {fileName}</p> : null}
        </div>

        {result ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-2">
            <p className="font-medium text-gray-800">
              Added {result.added ?? 0}. Not added {result.not_added ?? (skipped.length + failed.length)}
              {' '}({skipped.length} already existed, {failed.length} failed).
            </p>
            {skipped.slice(0, 15).map((item, i) => (
              <p key={`s-${i}`} className="text-xs text-amber-800">
                Row {item.row}: {item.reason}
              </p>
            ))}
            {failed.slice(0, 15).map((item, i) => (
              <p key={`f-${i}`} className="text-xs text-red-700">
                Row {item.row}: {item.reason}
              </p>
            ))}
            {(result.temporary_passwords || []).slice(0, 20).map((item, i) => (
              <p key={`p-${i}`} className="text-xs text-gray-800">
                Row {item.row}: {item.name} ({item.username}) one-time password {item.password}
              </p>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default BulkExcelModal;
